import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { API_BASE } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

export default function AdminCouponsTab() {
  const { getToken } = useAuth();

  const [campaignFilter, setCampaignFilter] = useState('');
  const [codes, setCodes] = useState<any[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [singleState, setSingleState] = useState({
    code: '',
    campaign: '',
    label: '',
    benefit_type: 'free_months',
    benefit_value: 3,
    audience: 'both',
    account_status: 'any',
    starts_at: '',
    ends_at: '',
    max_redemptions: '',
    per_user_cap: 1,
    stackable: false,
    referrer_id: '',
  });
  const [singleResult, setSingleResult] = useState<string | null>(null);
  const [creatingSingle, setCreatingSingle] = useState(false);

  const [bulkState, setBulkState] = useState({
    campaign: '',
    label: '',
    count: 100,
    format_prefix: '',
    format_length: 6,
    benefit_type: 'free_months',
    benefit_value: 3,
    audience: 'both',
    account_status: 'any',
    starts_at: '',
    ends_at: '',
    max_redemptions: 1,
    per_user_cap: 1,
    stackable: false,
    referrer_id: '',
  });
  const [bulkResult, setBulkResult] = useState<string[] | null>(null);
  const [creatingBulk, setCreatingBulk] = useState(false);

  const loadCodes = useCallback(async () => {
    setLoadingList(true);
    setListError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('unauthorized');
      const qs = campaignFilter ? `?campaign=${encodeURIComponent(campaignFilter)}` : '';
      const res = await fetch(`${API_BASE}/admin-coupons${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to list codes');
      setCodes(Array.isArray(data.codes) ? data.codes : []);
    } catch (e: any) {
      setListError(e.message || 'Failed to load');
    } finally {
      setLoadingList(false);
    }
  }, [campaignFilter, getToken]);

  useEffect(() => { loadCodes(); }, [loadCodes]);

  const toggleActive = useCallback(async (id: string, active: boolean) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('unauthorized');
      const res = await fetch(`${API_BASE}/admin-coupons`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ id, active }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to update');
      loadCodes();
    } catch (e) {
      alert((e as any).message || 'Failed to update');
    }
  }, [getToken, loadCodes]);

  const handleCreateSingle = useCallback(async () => {
    setCreatingSingle(true);
    setSingleResult(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('unauthorized');
      const payload: any = { mode: 'single', ...singleState };
      // Normalize empties
      if (!payload.code) delete payload.code;
      if (!payload.label) payload.label = null;
      if (!payload.starts_at) payload.starts_at = null;
      if (!payload.ends_at) payload.ends_at = null;
      if (!payload.max_redemptions) payload.max_redemptions = null;
      if (!payload.referrer_id) payload.referrer_id = null;

      const res = await fetch(`${API_BASE}/admin-coupons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to create code');
      setSingleResult(data.code || null);
      loadCodes();
    } catch (e) {
      alert((e as any).message || 'Failed to create');
    } finally {
      setCreatingSingle(false);
    }
  }, [getToken, singleState, loadCodes]);

  const handleCreateBulk = useCallback(async () => {
    setCreatingBulk(true);
    setBulkResult(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('unauthorized');
      const payload: any = {
        mode: 'bulk',
        campaign: bulkState.campaign,
        label: bulkState.label || null,
        count: Number(bulkState.count) || 1,
        format: { prefix: bulkState.format_prefix || bulkState.campaign, length: Number(bulkState.format_length) || 6, uppercase: true },
        benefit_type: bulkState.benefit_type,
        benefit_value: Number(bulkState.benefit_value) || 1,
        audience: bulkState.audience,
        account_status: bulkState.account_status,
        starts_at: bulkState.starts_at || null,
        ends_at: bulkState.ends_at || null,
        max_redemptions: Number(bulkState.max_redemptions) || 1,
        per_user_cap: Number(bulkState.per_user_cap) || 1,
        stackable: !!bulkState.stackable,
        referrer_id: bulkState.referrer_id || null,
      };
      const res = await fetch(`${API_BASE}/admin-coupons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to bulk create');
      setBulkResult(Array.isArray(data.codes) ? data.codes : []);
      loadCodes();
    } catch (e) {
      alert((e as any).message || 'Failed to bulk create');
    } finally {
      setCreatingBulk(false);
    }
  }, [getToken, bulkState, loadCodes]);

  const copyText = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  const csvFromList = (list: string[]) => ['code', ...list].join('\n');

  return (
    <div className="space-y-8">
      <Card className="border-slate-700 bg-slate-900">
        <CardHeader>
          <CardTitle className="text-white">Campaign Codes</CardTitle>
          <CardDescription className="text-slate-400">List and quick controls</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 mb-4">
            <div className="flex-1">
              <Label htmlFor="campaign-filter" className="text-white">Filter by campaign</Label>
              <Input id="campaign-filter" value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} placeholder="e.g. DIWALI-2026" className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" />
            </div>
            <Button onClick={loadCodes} className="bg-indigo-600 !text-white hover:bg-indigo-700">Refresh</Button>
          </div>
          {listError && <p className="text-sm text-red-400 mb-2">{listError}</p>}
          <div className="overflow-x-auto rounded-lg border border-slate-800">
            <table className="w-full text-sm text-white">
              <thead className="bg-slate-800/60 text-slate-200 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Campaign</th>
                  <th className="px-3 py-2 text-left">Label</th>
                  <th className="px-3 py-2 text-left">Benefit</th>
                  <th className="px-3 py-2 text-left">Audience</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Redemptions</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {loadingList ? (
                  <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400">Loading…</td></tr>
                ) : (
                  codes.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No codes found</td></tr>
                  ) : (
                    codes.map((c) => (
                      <tr key={c.id}>
                        <td className="px-3 py-2">{c.campaign || '—'}</td>
                        <td className="px-3 py-2">{c.label || '—'}</td>
                        <td className="px-3 py-2">{c.benefit_type} · {c.benefit_value}</td>
                        <td className="px-3 py-2">{c.audience}/{c.account_status}</td>
                        <td className="px-3 py-2">{c.active ? 'Active' : 'Paused'}</td>
                        <td className="px-3 py-2">{c.redemptions ?? 0}</td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="outline" className="border-slate-700 text-white hover:bg-slate-800 mr-2" onClick={() => toggleActive(c.id, !c.active)}>
                            {c.active ? 'Pause' : 'Activate'}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-slate-700 bg-slate-900">
          <CardHeader>
            <CardTitle className="text-white">Create Single Code</CardTitle>
            <CardDescription className="text-slate-400">Generate a specific or auto code with rules</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="s-campaign" className="text-white">Campaign</Label>
                <Input id="s-campaign" value={singleState.campaign} onChange={(e) => setSingleState({ ...singleState, campaign: e.target.value })} className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" placeholder="e.g. DIWALI-2026" />
              </div>
              <div>
                <Label htmlFor="s-label" className="text-white">Label</Label>
                <Input id="s-label" value={singleState.label} onChange={(e) => setSingleState({ ...singleState, label: e.target.value })} className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" placeholder="Optional label" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="s-code" className="text-white">Custom Code (optional)</Label>
                <Input id="s-code" value={singleState.code} onChange={(e) => setSingleState({ ...singleState, code: e.target.value })} className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" placeholder="Leave empty to auto-generate" />
              </div>
              <div>
                <Label htmlFor="s-benefit" className="text-white">Benefit</Label>
                <div className="grid grid-cols-2 gap-2">
                  <select id="s-benefit" value={singleState.benefit_type} onChange={(e) => setSingleState({ ...singleState, benefit_type: e.target.value })} className="border rounded-md border-slate-700 bg-slate-950 text-white px-2 py-2" aria-label="Benefit type" title="Benefit type">
                    <option value="free_months">Free months</option>
                    <option value="sessions">Sessions</option>
                  </select>
                  <Input type="number" value={singleState.benefit_value} onChange={(e) => setSingleState({ ...singleState, benefit_value: Number(e.target.value) })} className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="s-audience" className="text-white">Audience</Label>
                <select id="s-audience" value={singleState.audience} onChange={(e) => setSingleState({ ...singleState, audience: e.target.value })} className="border rounded-md border-slate-700 bg-slate-950 text-white px-2 py-2 w-full" aria-label="Audience" title="Audience">
                  <option value="both">Both</option>
                  <option value="new">New users</option>
                  <option value="existing">Existing users</option>
                </select>
              </div>
              <div>
                <Label htmlFor="s-status" className="text-white">Account Status</Label>
                <select id="s-status" value={singleState.account_status} onChange={(e) => setSingleState({ ...singleState, account_status: e.target.value })} className="border rounded-md border-slate-700 bg-slate-950 text-white px-2 py-2 w-full" aria-label="Account status" title="Account status">
                  <option value="any">Any</option>
                  <option value="trial">Trial</option>
                  <option value="paid">Paid</option>
                  <option value="lapsed">Lapsed</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Start / End</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="date" value={singleState.starts_at} onChange={(e) => setSingleState({ ...singleState, starts_at: e.target.value })} className="border-slate-700 bg-slate-950 text-white" />
                  <Input type="date" value={singleState.ends_at} onChange={(e) => setSingleState({ ...singleState, ends_at: e.target.value })} className="border-slate-700 bg-slate-950 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-white">Caps</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Input type="number" placeholder="Max redemptions" value={singleState.max_redemptions as any} onChange={(e) => setSingleState({ ...singleState, max_redemptions: e.target.value })} className="border-slate-700 bg-slate-950 text-white" />
                  <Input type="number" placeholder="Per user" value={singleState.per_user_cap as any} onChange={(e) => setSingleState({ ...singleState, per_user_cap: Number(e.target.value) })} className="border-slate-700 bg-slate-950 text-white" />
                  <div className="flex items-center gap-2 text-white">
                    <input id="s-stack" type="checkbox" checked={singleState.stackable} onChange={(e) => setSingleState({ ...singleState, stackable: e.target.checked })} />
                    <label htmlFor="s-stack">Stackable</label>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="s-ref" className="text-white">Referrer ID (optional)</Label>
              <Input id="s-ref" value={singleState.referrer_id} onChange={(e) => setSingleState({ ...singleState, referrer_id: e.target.value })} className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" placeholder="user_... or partner code" />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleCreateSingle} disabled={creatingSingle} className="bg-indigo-600 !text-white hover:bg-indigo-700">{creatingSingle ? 'Creating…' : 'Create Code'}</Button>
              {singleResult && (
                <>
                  <span className="text-xs text-slate-300">Created:</span>
                  <code className="rounded bg-slate-800 px-2 py-1 text-xs text-white">{singleResult}</code>
                  <Button size="sm" variant="outline" className="border-slate-700 text-white hover:bg-slate-800" onClick={() => copyText(singleResult)}>Copy</Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-700 bg-slate-900">
          <CardHeader>
            <CardTitle className="text-white">Bulk Generate Codes</CardTitle>
            <CardDescription className="text-slate-400">Create many codes for campaigns or referrals</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="b-campaign" className="text-white">Campaign</Label>
                <Input id="b-campaign" value={bulkState.campaign} onChange={(e) => setBulkState({ ...bulkState, campaign: e.target.value })} className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" placeholder="e.g. REFERRALS-Q4" />
              </div>
              <div>
                <Label htmlFor="b-label" className="text-white">Label</Label>
                <Input id="b-label" value={bulkState.label} onChange={(e) => setBulkState({ ...bulkState, label: e.target.value })} className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" placeholder="Optional label" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-white">Count</Label>
                <Input type="number" value={bulkState.count as any} onChange={(e) => setBulkState({ ...bulkState, count: Number(e.target.value) })} className="border-slate-700 bg-slate-950 text-white" />
              </div>
              <div>
                <Label className="text-white">Prefix</Label>
                <Input value={bulkState.format_prefix} onChange={(e) => setBulkState({ ...bulkState, format_prefix: e.target.value })} className="border-slate-700 bg-slate-950 text-white" placeholder="Defaults to campaign" />
              </div>
              <div>
                <Label className="text-white">Length</Label>
                <Input type="number" value={bulkState.format_length as any} onChange={(e) => setBulkState({ ...bulkState, format_length: Number(e.target.value) })} className="border-slate-700 bg-slate-950 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Benefit</Label>
                <div className="grid grid-cols-2 gap-2">
                  <select value={bulkState.benefit_type} onChange={(e) => setBulkState({ ...bulkState, benefit_type: e.target.value })} className="border rounded-md border-slate-700 bg-slate-950 text-white px-2 py-2" aria-label="Benefit type" title="Benefit type">
                    <option value="free_months">Free months</option>
                    <option value="sessions">Sessions</option>
                  </select>
                  <Input type="number" value={bulkState.benefit_value as any} onChange={(e) => setBulkState({ ...bulkState, benefit_value: Number(e.target.value) })} className="border-slate-700 bg-slate-950 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-white">Eligibility</Label>
                <div className="grid grid-cols-2 gap-2">
                  <select value={bulkState.audience} onChange={(e) => setBulkState({ ...bulkState, audience: e.target.value })} className="border rounded-md border-slate-700 bg-slate-950 text-white px-2 py-2" aria-label="Audience" title="Audience">
                    <option value="both">Both</option>
                    <option value="new">New users</option>
                    <option value="existing">Existing users</option>
                  </select>
                  <select value={bulkState.account_status} onChange={(e) => setBulkState({ ...bulkState, account_status: e.target.value })} className="border rounded-md border-slate-700 bg-slate-950 text-white px-2 py-2" aria-label="Account status" title="Account status">
                    <option value="any">Any</option>
                    <option value="trial">Trial</option>
                    <option value="paid">Paid</option>
                    <option value="lapsed">Lapsed</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Start / End</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="date" value={bulkState.starts_at} onChange={(e) => setBulkState({ ...bulkState, starts_at: e.target.value })} className="border-slate-700 bg-slate-950 text-white" />
                  <Input type="date" value={bulkState.ends_at} onChange={(e) => setBulkState({ ...bulkState, ends_at: e.target.value })} className="border-slate-700 bg-slate-950 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-white">Caps</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Input type="number" placeholder="Max redemptions" value={bulkState.max_redemptions as any} onChange={(e) => setBulkState({ ...bulkState, max_redemptions: Number(e.target.value) })} className="border-slate-700 bg-slate-950 text-white" />
                  <Input type="number" placeholder="Per user" value={bulkState.per_user_cap as any} onChange={(e) => setBulkState({ ...bulkState, per_user_cap: Number(e.target.value) })} className="border-slate-700 bg-slate-950 text-white" />
                  <div className="flex items-center gap-2 text-white">
                    <input id="b-stack" type="checkbox" checked={bulkState.stackable} onChange={(e) => setBulkState({ ...bulkState, stackable: e.target.checked })} />
                    <label htmlFor="b-stack">Stackable</label>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="b-ref" className="text-white">Referrer ID (optional)</Label>
              <Input id="b-ref" value={bulkState.referrer_id} onChange={(e) => setBulkState({ ...bulkState, referrer_id: e.target.value })} className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" placeholder="user_... or partner code" />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleCreateBulk} disabled={creatingBulk} className="bg-indigo-600 !text-white hover:bg-indigo-700">{creatingBulk ? 'Generating…' : 'Generate Codes'}</Button>
              {bulkResult && bulkResult.length > 0 && (
                <>
                  <span className="text-xs text-slate-300">Generated: {bulkResult.length}</span>
                  <Button size="sm" variant="outline" className="border-slate-700 text-white hover:bg-slate-800" onClick={() => copyText(bulkResult.join('\n'))}>Copy List</Button>
                  <a
                    href={`data:text/csv;charset=utf-8,${encodeURIComponent(csvFromList(bulkResult))}`}
                    download={`codes_${Date.now()}.csv`}
                    className="text-xs underline text-indigo-300"
                  >
                    Download CSV
                  </a>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
